import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { 
  Check, 
  X, 
  Crown, 
  Sparkles, 
  Users, 
  Building2,
  Rocket,
  Handshake,
  Mail,
  Star,
  Loader2,
  AlertTriangle,
  ArrowRight,
  Shield,
  Infinity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan configuration matching backend config.py
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    description: 'Per piccole associazioni che iniziano',
    icon: Users,
    color: 'slate',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600',
    borderColor: 'border-slate-200',
    price_monthly: 0,
    price_yearly: 0,
    max_volontari: 20,
    max_soggetti: 30,
    max_eventi: 10,
    storage: '100 MB',
    features: [
      { name: 'Gestione volontari e soci', included: true },
      { name: 'Gestione eventi', included: true },
      { name: 'Registrazione presenze', included: true },
      { name: 'Quote associative (visualizzazione)', included: true },
      { name: 'Dashboard base', included: true },
      { name: 'Kit Amministrativo ETS', included: true },
      { name: 'Comunicazioni Email/WhatsApp', included: false },
      { name: 'Certificati e Report PDF', included: false },
      { name: 'Modulo Governance', included: false },
      { name: 'Magazzino e Automezzi', included: false },
    ]
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Per associazioni in crescita',
    icon: Sparkles,
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-300',
    price_monthly: 9.99,
    price_yearly: 107.89,
    max_volontari: 60,
    max_soggetti: 100,
    max_eventi: 50,
    storage: '1 GB',
    features: [
      { name: 'Tutto del piano Free', included: true, highlight: true },
      { name: 'Comunicazioni Email/WhatsApp', included: true, highlight: true },
      { name: 'Kit Soci completo', included: true, highlight: true },
      { name: 'Archivio documenti', included: true, highlight: true },
      { name: 'Certificati base', included: true },
      { name: 'Report PDF', included: true },
      { name: 'Export (RUNTS)', included: true },
      { name: 'Calendario Scadenzario', included: true },
      { name: 'Modulo Governance', included: false },
      { name: 'Magazzino e Automezzi', included: false },
    ]
  },
  {
    id: 'governance',
    name: 'Governance',
    description: 'Per associazioni strutturate',
    icon: Building2,
    color: 'indigo',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-600',
    borderColor: 'border-indigo-300',
    popular: true,
    price_monthly: 14.99,
    price_yearly: 161.89,
    max_volontari: 100,
    max_soggetti: 200,
    max_eventi: 100,
    storage: '2 GB',
    features: [
      { name: 'Tutto del piano Premium', included: true, highlight: true },
      { name: 'Modulo Governance (Organi, Assemblee, Consiglio)', included: true, highlight: true, isNew: true },
      { name: 'Rendicontazione economica (35 categorie)', included: true, highlight: true },
      { name: 'Certificati personalizzati', included: true, highlight: true },
      { name: 'Comunicazioni avanzate e template', included: true },
      { name: 'Log operazioni completo', included: true },
      { name: 'Libri Sociali PDF', included: true },
      { name: 'Import Massivo (max 200 soggetti)', included: true },
      { name: 'Schemi Ministeriali Bilancio (MOD. D, C)', included: true },
      { name: 'Magazzino e Automezzi', included: false },
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Soluzione completa',
    icon: Crown,
    color: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-300',
    price_monthly: 24.99,
    price_yearly: 269.89,
    max_volontari: 250,
    max_soggetti: 500,
    max_eventi: -1,
    storage: '5 GB',
    features: [
      { name: 'Tutto del piano Governance', included: true, highlight: true },
      { name: 'Rendicontazione completa (120+ categorie)', included: true, highlight: true },
      { name: 'Statistiche avanzate con grafici', included: true, highlight: true },
      { name: 'Gestione Magazzino e Assegnazioni', included: true, highlight: true, isNew: true },
      { name: 'Gestione Automezzi (assicurazioni, revisioni, manutenzioni)', included: true, highlight: true, isNew: true },
      { name: 'Import Massivo CSV (max 500)', included: true },
      { name: 'Chatbot AI assistente', included: true },
      { name: 'Generazione batch certificati', included: true },
      { name: 'Schemi Ministeriali completi (MOD. A, B)', included: true },
      { name: 'Supporto prioritario', included: true },
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Per organizzazioni con esigenze illimitate',
    icon: Rocket,
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-300',
    price_monthly: 49.99,
    price_yearly: 539.89,
    max_volontari: -1,
    max_soggetti: -1,
    max_eventi: -1,
    storage: '20 GB',
    features: [
      { name: 'Tutto del piano Pro', included: true, highlight: true },
      { name: 'Volontari illimitati', included: true, highlight: true },
      { name: 'Soggetti illimitati', included: true, highlight: true },
      { name: 'Eventi illimitati', included: true, highlight: true },
      { name: 'Supporto prioritario via email', included: true },
    ]
  },
  {
    id: 'convenzioni',
    name: 'Convenzioni Nazionali',
    description: 'Per reti, federazioni e convenzioni',
    icon: Handshake,
    color: 'emerald',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-300',
    price_monthly: -1,
    price_yearly: -1,
    max_volontari: -1,
    max_soggetti: -1,
    max_eventi: -1,
    storage: '50 GB',
    contact_email: 'convenzioni@assietica.it',
    features: [
      { name: 'Tutto del piano Enterprise', included: true, highlight: true },
      { name: 'Gestione multi-ente', included: true, highlight: true },
      { name: 'Dashboard federazione', included: true, highlight: true },
      { name: 'Reportistica aggregata', included: true, highlight: true },
      { name: 'Pricing personalizzato', included: true },
      { name: 'Account manager dedicato', included: true },
      { name: 'Formazione inclusa', included: true },
    ]
  }
];

export default function Pricing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [yearlyBilling, setYearlyBilling] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Load subscription
      const subResponse = await fetch(`${API_URL}/api/subscription`, { 
        credentials: 'include',
        headers 
      });
      if (subResponse.ok) {
        const data = await subResponse.json();
        setSubscription(data.subscription);
      }

      // Load counts
      const countsResponse = await fetch(`${API_URL}/api/subscription/counts`, { 
        credentials: 'include',
        headers
      });
      if (countsResponse.ok) {
        const countsData = await countsResponse.json();
        setCounts(countsData);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    // Special handling for convenzioni - redirect to dedicated page
    if (planId === 'convenzioni') {
      navigate('/convenzioni');
      return;
    }

    const currentPlan = subscription?.plan || 'free';
    const planOrder = ['free', 'premium', 'governance', 'pro', 'enterprise', 'convenzioni'];
    const isDowngrade = planOrder.indexOf(planId) < planOrder.indexOf(currentPlan);

    if (isDowngrade) {
      if (!window.confirm('Sei sicuro di voler effettuare il downgrade? Perderai l\'accesso ad alcune funzionalità.')) {
        return;
      }
    }

    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = isDowngrade ? '/api/subscription/downgrade' : '/api/subscription/upgrade';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ plan: planId, billing_cycle: yearlyBilling ? 'yearly' : 'monthly' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
        toast.success(`Piano aggiornato a ${PLANS.find(p => p.id === planId)?.name || planId}`);
        loadSubscription();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore durante l\'aggiornamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setUpgrading(false);
    }
  };

  const currentPlan = subscription?.plan || 'free';
  const planOrder = ['free', 'premium', 'governance', 'pro', 'enterprise', 'convenzioni'];

  const getButtonText = (planId) => {
    if (planId === currentPlan) return 'Piano Attuale';
    if (planId === 'convenzioni') return 'Contattaci';
    if (planOrder.indexOf(planId) < planOrder.indexOf(currentPlan)) return 'Downgrade';
    return 'Attiva';
  };

  const getButtonVariant = (planId) => {
    if (planId === currentPlan) return 'secondary';
    if (planOrder.indexOf(planId) < planOrder.indexOf(currentPlan)) return 'outline';
    return 'default';
  };

  const formatLimit = (value) => {
    if (value === -1) return <Infinity className="w-4 h-4 inline" />;
    return value;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pricing-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Piani e Prezzi
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl mx-auto">
          Scegli il piano più adatto alle esigenze della tua organizzazione. 
          Basato su <strong>volontari attivi</strong> e <strong>soggetti unici gestiti</strong>.
        </p>
      </div>

      {/* Current Plan Status with Counts */}
      {counts && (
        <Card className={`${counts.upgrade_required ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'}`}>
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${counts.upgrade_required ? 'bg-amber-500' : 'bg-[#0055BB]'}`}>
                  {currentPlan === 'enterprise' || currentPlan === 'convenzioni' ? <Rocket className="w-6 h-6 text-white" /> :
                   currentPlan === 'pro' ? <Crown className="w-6 h-6 text-white" /> : 
                   currentPlan === 'governance' ? <Building2 className="w-6 h-6 text-white" /> :
                   currentPlan === 'premium' ? <Sparkles className="w-6 h-6 text-white" /> :
                   <Users className="w-6 h-6 text-white" />}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Piano attuale</p>
                  <p className={`text-xl font-bold ${counts.upgrade_required ? 'text-amber-600' : 'text-[#0055BB]'}`}>
                    {counts.plan_name}
                  </p>
                </div>
              </div>
              
              {/* Usage Stats */}
              <div className="flex flex-wrap gap-4 lg:gap-6">
                <div className="text-center min-w-[80px]">
                  <p className="text-xs text-slate-500">Volontari Attivi</p>
                  <p className={`font-bold text-lg ${counts.exceeded?.volontari ? 'text-red-600' : ''}`}>
                    {counts.counts?.volontari_attivi}/{counts.limits?.max_volontari_attivi === -1 ? '∞' : counts.limits?.max_volontari_attivi}
                  </p>
                  {counts.limits?.max_volontari_attivi !== -1 && (
                    <Progress 
                      value={Math.min((counts.counts?.volontari_attivi / counts.limits?.max_volontari_attivi) * 100, 100)} 
                      className={`w-20 h-1.5 mt-1 ${counts.exceeded?.volontari ? '[&>div]:bg-red-500' : ''}`}
                    />
                  )}
                </div>
                <div className="text-center min-w-[80px]">
                  <p className="text-xs text-slate-500">Soggetti Unici</p>
                  <p className={`font-bold text-lg ${counts.exceeded?.soggetti ? 'text-red-600' : ''}`}>
                    {counts.counts?.soggetti_unici}/{counts.limits?.max_soggetti_unici === -1 ? '∞' : counts.limits?.max_soggetti_unici}
                  </p>
                  {counts.limits?.max_soggetti_unici !== -1 && (
                    <Progress 
                      value={Math.min((counts.counts?.soggetti_unici / counts.limits?.max_soggetti_unici) * 100, 100)} 
                      className={`w-20 h-1.5 mt-1 ${counts.exceeded?.soggetti ? '[&>div]:bg-red-500' : ''}`}
                    />
                  )}
                </div>
                <div className="text-center min-w-[80px]">
                  <p className="text-xs text-slate-500">Eventi</p>
                  <p className="font-bold text-lg">
                    {counts.counts?.eventi}/{counts.limits?.max_eventi === -1 ? '∞' : counts.limits?.max_eventi}
                  </p>
                </div>
              </div>

              {counts.upgrade_required && (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-100 px-4 py-2 rounded-lg">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">Upgrade consigliato: {counts.suggested_plan_name}</p>
                    {counts.in_grace_period && counts.grace_until && (
                      <p className="text-xs">Hai tempo fino al {counts.grace_until}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <Label className={!yearlyBilling ? 'font-bold' : ''}>Mensile</Label>
        <Switch 
          checked={yearlyBilling} 
          onCheckedChange={setYearlyBilling}
          data-testid="billing-toggle"
        />
        <Label className={yearlyBilling ? 'font-bold' : ''}>
          Annuale
          <Badge className="ml-2 bg-green-100 text-green-700 hover:bg-green-100">Risparmia 10%</Badge>
        </Label>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
        <p className="text-sm text-blue-800">
          <Shield className="w-4 h-4 inline mr-1" />
          Il passaggio al piano successivo avviene se superi <strong>anche uno solo</strong> dei due limiti: volontari attivi o soggetti unici gestiti.
        </p>
      </div>

      {/* Pricing Cards - 3x2 Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PLANS.map((plan) => {
          const price = yearlyBilling ? plan.price_yearly : plan.price_monthly;
          const isCurrentPlan = plan.id === currentPlan;
          const IconComponent = plan.icon;
          const isCustomPricing = plan.price_monthly === -1;
          
          return (
            <Card 
              key={plan.id} 
              className={`relative flex flex-col ${plan.popular ? 'border-2 border-indigo-400 shadow-lg' : ''} ${isCurrentPlan ? 'ring-2 ring-[#0055BB]' : ''} ${plan.borderColor}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-indigo-600 hover:bg-indigo-600">
                    <Star className="w-3 h-3 mr-1" />
                    Consigliato
                  </Badge>
                </div>
              )}
              
              {isCurrentPlan && (
                <div className="absolute -top-3 right-4">
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <Check className="w-3 h-3 mr-1" />
                    Attivo
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${plan.bgColor}`}>
                  <IconComponent className={`w-6 h-6 ${plan.textColor}`} />
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription className="text-xs">{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="text-center flex-grow">
                <div className="mb-4">
                  {isCustomPricing ? (
                    <div>
                      <span className="text-2xl font-bold">Su richiesta</span>
                      <p className="text-xs text-slate-500 mt-1">Contattaci per un preventivo</p>
                    </div>
                  ) : (
                    <>
                      <span className="text-3xl font-bold">
                        {price === 0 ? 'Gratis' : `€${price.toFixed(2)}`}
                      </span>
                      {price > 0 && (
                        <>
                          <span className="text-slate-500 text-sm">/{yearlyBilling ? 'anno' : 'mese'}</span>
                          <p className="text-xs text-slate-400 mt-1">IVA inclusa</p>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Limits */}
                <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-slate-50 rounded-lg text-xs">
                  <div>
                    <p className="text-slate-500">Volontari</p>
                    <p className="font-bold">{formatLimit(plan.max_volontari)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Soggetti</p>
                    <p className="font-bold">{formatLimit(plan.max_soggetti)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Eventi/anno</p>
                    <p className="font-bold">{formatLimit(plan.max_eventi)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Storage</p>
                    <p className="font-bold">{plan.storage}</p>
                  </div>
                </div>
                
                <ul className="space-y-2 text-left text-xs">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={`flex items-start gap-2 ${feature.highlight ? 'font-medium' : ''}`}>
                      {feature.included ? (
                        <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${feature.highlight ? 'text-green-600' : 'text-green-500'}`} />
                      ) : (
                        <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-300" />
                      )}
                      <span className={feature.included ? '' : 'text-slate-400'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter className="mt-auto">
                <Button 
                  className={`w-full ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                  variant={getButtonVariant(plan.id)}
                  disabled={plan.id === currentPlan || upgrading}
                  onClick={() => handleUpgrade(plan.id)}
                  data-testid={`btn-select-${plan.id}`}
                >
                  {upgrading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {plan.id === 'convenzioni' && <Mail className="w-4 h-4 mr-1" />}
                  {getButtonText(plan.id)}
                  {plan.id !== currentPlan && plan.id !== 'convenzioni' && <ArrowRight className="w-4 h-4 ml-1" />}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Soggetti Unici Explanation */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <h3 className="font-bold text-lg mb-3">Cosa sono i "Soggetti Unici Gestiti"?</h3>
          <p className="text-sm text-slate-600 mb-3">
            Per <strong>soggetti unici gestiti</strong> si intende il numero di persone uniche presenti nel sistema, 
            <strong> senza doppio conteggio</strong> nel caso in cui la stessa persona ricopra più ruoli:
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">Socio</Badge>
            <Badge variant="outline">Associato</Badge>
            <Badge variant="outline">Volontario</Badge>
            <Badge variant="outline">Consigliere</Badge>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Esempio: Mario Rossi è sia socio che volontario? Conta come 1 soggetto unico, non 2.
          </p>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <div className="mt-12">
        <h2 className="text-xl font-bold text-center mb-6">Domande Frequenti</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Quando devo fare l'upgrade?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Quando superi anche <strong>uno solo</strong> dei due limiti (volontari attivi O soggetti unici). 
                Avrai comunque 3 mesi di tempo per decidere.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Posso fare downgrade?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Sì, ma solo se i tuoi numeri rientrano nei limiti del piano inferiore. 
                Non perderai i dati, ma alcune funzionalità saranno disabilitate.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Come funziona la fatturazione?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Pagando annualmente risparmi circa 2 mesi. Accettiamo carte di credito/debito 
                tramite Stripe. Riceverai fattura elettronica.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Cos'è il piano Convenzioni Nazionali?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                È riservato a federazioni, reti e grandi organizzazioni con esigenze particolari. 
                Contatta <a href="mailto:convenzioni@assietica.it" className="text-[#0055BB]">convenzioni@assietica.it</a> per un preventivo.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-8 bg-slate-50 rounded-lg">
        <p className="text-slate-600 mb-4">
          Hai domande? Contattaci a <a href="mailto:supporto@assietica.it" className="text-[#0055BB] font-medium">supporto@assietica.it</a>
        </p>
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          Torna alla Dashboard
        </Button>
      </div>
    </div>
  );
}
